import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBookingApi } from './bookingApi';
import BookingDetailModal from './BookingDetailModal';
import {
    BOOKING_STATUS_LABELS,
    BOOKING_STATUS_TONE,
    formatTimeOnly,
    bookingCustomerLabel,
} from './bookingFormat';

/**
 * Month calendar of bookings across all calendars. Bookings are bucketed by
 * their start day in the viewer's local timezone so the whole grid shares one
 * reference frame; selecting a day lists that day's bookings below (which also
 * makes it usable on mobile, where per-cell chips collapse to dots).
 * Semi-independent — fetches its own bookings for the visible month.
 */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_CHIPS = 3;

function localKey(date) {
    const d = new Date(date);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function buildGrid(year, month) {
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const gridStart = new Date(year, month, 1 - startOffset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
        cells.push(date);
    }
    return cells;
}

export default function BookingCalendar({ resources = [] }) {
    const api = useBookingApi();
    const today = useMemo(() => new Date(), []);
    const [cursor, setCursor] = useState(() => ({ year: today.getFullYear(), month: today.getMonth() }));
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resourceId, setResourceId] = useState('');
    const [selectedDay, setSelectedDay] = useState(() => localKey(today));
    const [selectedBooking, setSelectedBooking] = useState(null);

    const cells = useMemo(() => buildGrid(cursor.year, cursor.month), [cursor]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const from = new Date(cursor.year, cursor.month, 1);
            const to = new Date(cursor.year, cursor.month + 1, 1);
            const filters = { from: from.toISOString(), to: to.toISOString(), limit: 500 };
            if (resourceId) filters.resourceId = resourceId;
            const response = await api.listBookings(filters);
            setBookings(response.data || []);
        } catch (err) {
            setError(err.message || 'Could not load bookings.');
        } finally {
            setLoading(false);
        }
    }, [api, cursor, resourceId]);

    useEffect(() => { load(); }, [load]);

    const byDay = useMemo(() => {
        const map = new Map();
        bookings.forEach(booking => {
            const key = localKey(booking.startAt);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(booking);
        });
        // keep each day's bookings time-ordered
        map.forEach(list => list.sort((a, b) => new Date(a.startAt) - new Date(b.startAt)));
        return map;
    }, [bookings]);

    const handleUpdated = (updated) => {
        if (!updated) { load(); return; }
        setBookings(prev => prev.map(b => (
            b.id === updated.id ? { ...b, ...updated, resource: b.resource, product: b.product } : b
        )));
    };

    const monthLabel = new Date(cursor.year, cursor.month, 1)
        .toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    const todayKey = localKey(today);
    const selectedList = byDay.get(selectedDay) || [];

    const step = (delta) => setCursor(prev => {
        const d = new Date(prev.year, prev.month + delta, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });

    const goToday = () => {
        setCursor({ year: today.getFullYear(), month: today.getMonth() });
        setSelectedDay(todayKey);
    };

    return (
        <div className="booking-calendar">
            <div className="booking-pane-header">
                <div className="booking-cal-nav">
                    <button type="button" className="booking-iconbtn" onClick={() => step(-1)} aria-label="Previous month">
                        <i className="fas fa-chevron-left" aria-hidden="true"></i>
                    </button>
                    <strong className="booking-cal-month">{monthLabel}</strong>
                    <button type="button" className="booking-iconbtn" onClick={() => step(1)} aria-label="Next month">
                        <i className="fas fa-chevron-right" aria-hidden="true"></i>
                    </button>
                    <button type="button" className="button secondary small" onClick={goToday}>Today</button>
                </div>
                <div className="booking-cal-tools">
                    {resources.length > 0 && (
                        <select value={resourceId} onChange={e => setResourceId(e.target.value)} aria-label="Calendar filter">
                            <option value="">All calendars</option>
                            {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    )}
                    <button type="button" className="button secondary small" onClick={load} disabled={loading}>
                        <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            {error && <div className="error booking-inline-error">{error}</div>}

            <div className="booking-cal-grid" role="grid">
                {WEEKDAYS.map(day => (
                    <div key={day} className="booking-cal-weekday" role="columnheader">{day}</div>
                ))}
                {cells.map(date => {
                    const key = localKey(date);
                    const dayBookings = byDay.get(key) || [];
                    const inMonth = date.getMonth() === cursor.month;
                    return (
                        <button
                            key={key}
                            type="button"
                            role="gridcell"
                            className={[
                                'booking-cal-cell',
                                inMonth ? '' : 'is-outside',
                                key === todayKey ? 'is-today' : '',
                                key === selectedDay ? 'is-selected' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => setSelectedDay(key)}
                        >
                            <span className="booking-cal-daynum">{date.getDate()}</span>
                            {dayBookings.length > 0 && (
                                <>
                                    <span className="booking-cal-chips">
                                        {dayBookings.slice(0, MAX_CHIPS).map(b => (
                                            <span
                                                key={b.id}
                                                className={`booking-cal-chip tone-${BOOKING_STATUS_TONE[b.status] || 'muted'}`}
                                                style={{ borderLeftColor: b.resource?.color || 'var(--bk-accent)' }}
                                            >
                                                <span className="booking-cal-chip-time">{formatTimeOnly(b.startAt)}</span>
                                                <span className="booking-cal-chip-title">
                                                    {b.product?.name || bookingCustomerLabel(b)}
                                                </span>
                                            </span>
                                        ))}
                                        {dayBookings.length > MAX_CHIPS && (
                                            <span className="booking-cal-more">+{dayBookings.length - MAX_CHIPS} more</span>
                                        )}
                                    </span>
                                    <span className="booking-cal-dots" aria-hidden="true">
                                        {dayBookings.slice(0, 4).map(b => (
                                            <span
                                                key={b.id}
                                                className="booking-cal-dot"
                                                style={{ backgroundColor: b.resource?.color || 'var(--bk-accent)' }}
                                            />
                                        ))}
                                    </span>
                                </>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="booking-cal-daypanel">
                <h4>
                    {new Date(selectedDay + 'T00:00:00').toLocaleDateString(undefined, {
                        weekday: 'long', month: 'long', day: 'numeric',
                    })}
                </h4>
                {selectedList.length === 0 ? (
                    <p className="booking-muted">No bookings this day.</p>
                ) : (
                    <ul className="booking-agenda-list">
                        {selectedList.map(booking => {
                            const tone = BOOKING_STATUS_TONE[booking.status] || 'muted';
                            return (
                                <li key={booking.id}>
                                    <button type="button" className="booking-agenda-row" onClick={() => setSelectedBooking(booking)}>
                                        <span className="booking-agenda-time">{formatTimeOnly(booking.startAt)}</span>
                                        <span
                                            className="booking-agenda-bar"
                                            style={{ backgroundColor: booking.resource?.color || 'var(--bk-accent)' }}
                                            aria-hidden="true"
                                        />
                                        <span className="booking-agenda-info">
                                            <span className="booking-agenda-title">
                                                {booking.product?.name || bookingCustomerLabel(booking)}
                                            </span>
                                            <span className="booking-agenda-sub">
                                                {booking.resource?.name || `Calendar #${booking.resourceId}`}
                                            </span>
                                        </span>
                                        <span className={`booking-badge ${tone}`}>
                                            {BOOKING_STATUS_LABELS[booking.status] || booking.status}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {selectedBooking && (
                <BookingDetailModal
                    booking={selectedBooking}
                    onUpdated={handleUpdated}
                    onClose={() => setSelectedBooking(null)}
                />
            )}
        </div>
    );
}
